import React, { createContext, useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { collection, doc, onSnapshot, updateDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { useFirebase } from '../firebase/firebase-provider';
import { useDataStore } from './datastore-provider';
import { useAuth } from './auth-provider';
import { useSettings } from './settings-provider';

interface FriendshipsContextType {
    friends: any[] | null;
    receivedInvitations: any[] | null;
    sentInvitations: any[] | null;
    friendships: any[] | null;
    removeFriendship: (id: any) => void;
    blockUser: (id: any) => void;
    acceptFriendship: (id: any) => void;
    addFriend: (friend: { id: any; username: any }) => void;
    getFriend: (id: any) => any;
}

const FriendshipsContext = createContext<FriendshipsContextType | undefined>(undefined);

export const useFriendships = () => {
    const context = React.useContext(FriendshipsContext);
    if (context === undefined) {
        throw new Error('useFriendships must be used within a FriendshipsProvider');
    }
    return context;
};

const COLLECTION_NAME = 'friendships';

export const FRIENDSHIP_STATUS = {
    SENT: 'SENT',
    RECEIVED: 'RECEIVED',
    FRIEND: 'FRIEND',
    BLOCKED: 'BLOCKED',
};

const DEFAULT_FRIENDSHIP = { status: FRIENDSHIP_STATUS.RECEIVED };

FriendshipsProvider.propTypes = {
    children: PropTypes.element.isRequired,
};
export function FriendshipsProvider(props: any) {
    const [friendships, setFriendships] = useState(null);
    const [friends, setFriends] = useState(null);
    const [receivedInvitations, setReceivedInvitations] = useState(null);
    const [sentInvitations, setSentInvitations] = useState(null);
    const app = useFirebase();
    const { userDb, getUserStore } = useDataStore();
    const { currentUser } = useAuth();
    const { publicSettings, getUsersById } = useSettings();
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (userDb && currentUser) {
            const db = getFirestore(app);
            const friendshipsCollection = collection(db, 'users', currentUser.uid, COLLECTION_NAME);

            const unsubscribe = onSnapshot(
                friendshipsCollection,
                (querySnapshot: any) => {
                    if (!querySnapshot.docs || !querySnapshot.docs.length) {
                        setFriendships([]);
                    } else {
                        setFriendships(loadFriendships(querySnapshot.docs));
                    }
                },
                (error: any) => {
                    console.debug(error);
                    setFriendships(friendships || []);
                    // TODO: Let the user know something went wrong
                }
            );

            return () => unsubscribe();
        } else {
            setFriendships(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDb, currentUser]);

    useEffect(() => {
        if (friendships) {
            const friendUsernames: any = [];
            const receivedUsernames: any = [];
            const sentUsernames: any = [];
            friendships.forEach((f: any) => {
                switch (f.status) {
                case FRIENDSHIP_STATUS.FRIEND:
                    friendUsernames.push(f.id);
                    break;
                case FRIENDSHIP_STATUS.RECEIVED:
                    receivedUsernames.push(f.id);
                    break;
                case FRIENDSHIP_STATUS.SENT:
                    sentUsernames.push(f.id);
                    break;
                default:
                }
            });

            getUsersById(friendUsernames).then(setFriends);
            getUsersById(receivedUsernames).then(setReceivedInvitations);
            getUsersById(sentUsernames).then(setSentInvitations);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [friendships]);

    function loadFriendships(docs: any) {
        return docs.map((doc: any) => ({
            ...DEFAULT_FRIENDSHIP,
            id: doc.id,
            ...(doc.data() || [])
        }));
    }

    function blockUser(id: any) {
        setFriendshipStatus(id, FRIENDSHIP_STATUS.BLOCKED);
    }

    function acceptFriendship(id: any) {
        if (currentUser) {
            const db = getFirestore(app);
            const friendDocRef = doc(db, 'users', id, COLLECTION_NAME, currentUser.uid);
            updateDoc(friendDocRef, { status: FRIENDSHIP_STATUS.FRIEND })
                .then(() => {
                    setFriendshipStatus(id, FRIENDSHIP_STATUS.FRIEND);
                });
        }
    }

    function setFriendshipStatus(id: any, status: any) {
        const friendship = friendships.find((f: any) => f.id === id);
        if (currentUser && friendship) {
            const db = getFirestore(app);
            const friendshipDocRef = doc(db, 'users', currentUser.uid, COLLECTION_NAME, id);
            updateDoc(friendshipDocRef, { username: friendship.username, status })
                .catch(console.debug); // TODO: show error message
        }
    }

    function addFriend({
        id,
        username
    }: any) {
        if (id === currentUser.uid) return; // Can't add yourself silly
        if (currentUser) {
            const db = getFirestore(app);
            const friendReceivedRef = doc(db, 'users', id, COLLECTION_NAME, currentUser.uid);
            const friendSentRef = doc(db, 'users', currentUser.uid, COLLECTION_NAME, id);

            setDoc(friendReceivedRef, {
                status: FRIENDSHIP_STATUS.RECEIVED,
                username: publicSettings.username.trim().toLocaleLowerCase()
            })
                .then(() => {
                    const newFriend = { status: FRIENDSHIP_STATUS.SENT, username: username.trim().toLocaleLowerCase() };
                    setDoc(friendSentRef, newFriend)
                        .then(() => {
                            enqueueSnackbar('Invite sent!'); // TODO: Email invite
                        })
                        .catch(console.debug); // TODO: show error message
                })
                .catch(console.debug); // TODO: show error message
        }
    }

    function removeFriendship(id: any) {
        if (currentUser) {
            const db = getFirestore(app);
            const friendDocRef = doc(db, 'users', id, COLLECTION_NAME, currentUser.uid);
            const myFriendDocRef = doc(db, 'users', currentUser.uid, COLLECTION_NAME, id);

            deleteDoc(friendDocRef)
                .then(() => {
                    deleteDoc(myFriendDocRef)
                        .then(() => {
                            enqueueSnackbar('Friend Removed');
                        })
                        .catch(console.debug); // TODO: show error message
                })
                .catch(console.debug); // TODO: show error message
        }
    }

    function getFriend(id: any) {
        return friends.find((friend: any) => friend.id === id);
    }

    return (
        <FriendshipsContext.Provider value={{ friends, receivedInvitations, sentInvitations, friendships, removeFriendship, blockUser, acceptFriendship, addFriend, getFriend }}>
            { props.children }
        </FriendshipsContext.Provider>
    );
}
